import * as THREE from "three";

/**
 * Sub-meter ground-detail shader injected into the terrain's MeshLambertMaterial
 * via onBeforeCompile. At gameplay zoom (~30 px/m) the 1m vertex grid is visibly
 * featureless; this adds procedural value-noise grain at multiple sub-meter scales
 * so the ground reads as "textured" without sourcing/tiling an image. It multiplies
 * into the existing biome vertex colors so grass/cliff/sand/snow each get
 * differentiated detail "for free."
 *
 * On grass it ALSO mixes toward two named tones — a darker shadow-green and a warm
 * dirt — so the field reads as painted-ground rather than one uniform saturated
 * green. Two layers stacked at very different scales: shadow follows the existing
 * fbm (sub-meter noise dark valleys), dirt patches use a much larger ~12m-cell
 * noise so they read as occasional clearings instead of uniform speckle. Both are
 * gated by a "greenness" mask on vColor so cliff/snow/sand/path keep their tone.
 *
 * STEEP-SLOPE ROCK (2026-07-02): on the same steep band that terrain.ts tints brown
 * (BIOME_ROCK / CLIFF_SLOPE_BAND), we blend a rocky-plate TEXTURE so a cliff face
 * reads as a broken rock wall instead of flat brown paint — the KTS hillside look
 * (Hill_Refernces_KTS). The texture is projected TRIPLANAR (world XY/YZ/XZ blended
 * by the world normal) rather than by the terrain's XZ world-pos: a near-vertical
 * face has almost no XZ variation, so an XZ (top-down) projection smears the texture
 * into vertical streaks — the classic UV-stretch failure. Triplanar lets the side
 * planes (which carry the Y coord) dominate on steep faces, so the plates run DOWN
 * the wall correctly. The texture is used as a MEAN-NORMALISED modulator (multiplies
 * the existing brown vColor) — it carries relief + a little rocky chroma, but not the
 * texture's absolute (bright tan) colour, so the earthy palette + toon/flat-shaded,
 * day/night-lit look are preserved. The rock mask is recomputed here from the world
 * normal (matching CLIFF_SLOPE_BAND) so it feathers per-pixel and auto-zeroes on the
 * flat skirt (which shares this material but has no steep faces).
 */

// ── Tunable rock knobs (dial by eye; live-settable via window.__rockTune in dev) ──
// Texture tile size in world metres — smaller = more/finer plates. ~6m reads as
// chunky broken rock at gameplay zoom without turning to noise.
const ROCK_TILE_METERS = 6.0;
// How strongly the rock texture modulates the brown base within the steep band
// (0 = pure flat brown, 1 = full texture relief). Kept below 1 so it stays earthy.
const ROCK_STRENGTH = 0.85;
// Pull the modulation toward greyscale relief (0 = keep the texture's tan chroma,
// 1 = pure light/dark relief). A little chroma warms the rock; too much reads sandy.
const ROCK_DESAT = 0.45;
// Mean luminance of Rocks_Desert_Diffuse (sRGB-decoded). Used to normalise the
// texture to ~1.0 so the multiply neither brightens nor darkens the band overall.
const ROCK_MEAN_LUM = 0.5;
// Steep-slope band on (1 - worldNormal.y): must match CLIFF_SLOPE_BAND in terrain.ts
// so the rock TEXTURE covers exactly the brown-tinted faces.
const ROCK_BAND = new THREE.Vector2(0.40, 0.72);

// Shared, lazily-loaded rock texture (one GPU upload for terrain + skirt).
let rockTex: THREE.Texture | null = null;
function getRockTexture(): THREE.Texture {
  if (rockTex) return rockTex;
  rockTex = new THREE.TextureLoader().load("/assets/nature/Rocks_Desert_Diffuse.png");
  rockTex.wrapS = rockTex.wrapT = THREE.RepeatWrapping;
  rockTex.colorSpace = THREE.SRGBColorSpace;
  rockTex.anisotropy = 8; // crisp on the grazing cliff faces
  return rockTex;
}

// Live shaders so window.__rockTune can retune the whole terrain (mesh + skirt)
// without a rebuild during by-eye passes. Dev affordance only.
const liveShaders: THREE.WebGLProgramParametersWithUniforms[] = [];
declare global {
  interface Window {
    __rockTune?: (p: Partial<{ scale: number; strength: number; desat: number; meanLum: number; band0: number; band1: number }>) => void;
  }
}
if (typeof window !== "undefined") {
  window.__rockTune = (p) => {
    for (const s of liveShaders) {
      if (p.scale !== undefined) s.uniforms.uRockScale!.value = 1 / p.scale;
      if (p.strength !== undefined) s.uniforms.uRockStrength!.value = p.strength;
      if (p.desat !== undefined) s.uniforms.uRockDesat!.value = p.desat;
      if (p.meanLum !== undefined) s.uniforms.uRockMeanLum!.value = p.meanLum;
      if (p.band0 !== undefined || p.band1 !== undefined) {
        const b = s.uniforms.uRockBand!.value as THREE.Vector2;
        if (p.band0 !== undefined) b.x = p.band0;
        if (p.band1 !== undefined) b.y = p.band1;
      }
    }
    // eslint-disable-next-line no-console
    console.log("[rockTune] applied to", liveShaders.length, "shaders", p);
  };
}

export function applyTerrainDetailShader(mat: THREE.MeshLambertMaterial): void {
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uMottleDark = { value: new THREE.Color(0x4a7028) };
    shader.uniforms.uMottleDirt = { value: new THREE.Color(0x6b5a3a) };
    shader.uniforms.uRockMap = { value: getRockTexture() };
    shader.uniforms.uRockScale = { value: 1 / ROCK_TILE_METERS };
    shader.uniforms.uRockStrength = { value: ROCK_STRENGTH };
    shader.uniforms.uRockDesat = { value: ROCK_DESAT };
    shader.uniforms.uRockMeanLum = { value: ROCK_MEAN_LUM };
    shader.uniforms.uRockBand = { value: ROCK_BAND.clone() };
    liveShaders.push(shader);

    shader.vertexShader =
      "varying vec3 vTerrainWorldPos;\n" +
      "varying vec3 vTerrainWorldNormal;\n" +
      shader.vertexShader.replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
        vTerrainWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
        // World-space normal (terrain has no rotation/scale, so mat3(modelMatrix)
        // is effectively identity, but compute it properly regardless).
        vTerrainWorldNormal = normalize(mat3(modelMatrix) * normal);`,
      );
    shader.fragmentShader =
      `varying vec3 vTerrainWorldPos;
      varying vec3 vTerrainWorldNormal;
      uniform vec3 uMottleDark;
      uniform vec3 uMottleDirt;
      uniform sampler2D uRockMap;
      uniform float uRockScale;
      uniform float uRockStrength;
      uniform float uRockDesat;
      uniform float uRockMeanLum;
      uniform vec2 uRockBand;
      float terrainHash21(vec2 p) {
        p = fract(p * vec2(123.34, 456.21));
        p += dot(p, p + 45.32);
        return fract(p.x * p.y);
      }
      float terrainValueNoise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        float a = terrainHash21(i);
        float b = terrainHash21(i + vec2(1.0, 0.0));
        float c = terrainHash21(i + vec2(0.0, 1.0));
        float d = terrainHash21(i + vec2(1.0, 1.0));
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
      }
      float terrainFbm(vec2 p) {
        // 3 octaves: ~1m broad, ~0.25m mid, ~0.06m pixel-grain
        return terrainValueNoise(p) * 0.5
             + terrainValueNoise(p * 4.0) * 0.3
             + terrainValueNoise(p * 16.0) * 0.2;
      }
      // Triplanar sample of the rock map: blend the three world-plane projections by
      // the (sharpened) world normal so steep faces sample the vertical planes and
      // never smear. Returns the raw sRGB-decoded rock colour.
      vec3 triplanarRock(vec3 wpos, vec3 wn, float scale) {
        vec3 bw = pow(abs(wn), vec3(4.0));   // sharpen: the dominant axis wins
        bw /= (bw.x + bw.y + bw.z);
        vec3 cx = texture2D(uRockMap, wpos.zy * scale).rgb;
        vec3 cy = texture2D(uRockMap, wpos.xz * scale).rgb;
        vec3 cz = texture2D(uRockMap, wpos.xy * scale).rgb;
        return cx * bw.x + cy * bw.y + cz * bw.z;
      }
      ` + shader.fragmentShader.replace(
        "#include <color_fragment>",
        `#include <color_fragment>
        float groundDetail = terrainFbm(vTerrainWorldPos.xz);
        // ±18% brightness modulation reads as textured ground at MOBA zoom
        // without overpowering the biome base color.
        diffuseColor.rgb *= mix(0.82, 1.18, groundDetail);

        // Greenness mask: positive only where vColor is more green than
        // red/blue (grass + path inherit a touch). Cliff/snow/sand collapse
        // to ~0 so their tones stay clean.
        float grassness = clamp((vColor.g - max(vColor.r, vColor.b)) * 4.0, 0.0, 1.0);

        // Layer 1: shadow patches in the dark valleys of the existing fbm.
        // smoothstep(hi, lo, x) — reversed args = inverted ramp, peaks where
        // groundDetail is small (~the same regions already darkened above).
        float shadowMask = smoothstep(0.45, 0.20, groundDetail);
        diffuseColor.rgb = mix(diffuseColor.rgb, uMottleDark, shadowMask * grassness * 0.35);

        // Layer 2: sparse dirt blotches. Cell size ~12.5m (0.08 freq) so they
        // appear as a handful of patches across the playfield, not speckle.
        // Threshold 0.58→0.78 keeps only the top tail of the noise → sparse.
        float dirtNoise = terrainValueNoise(vTerrainWorldPos.xz * 0.08);
        float dirtMask = smoothstep(0.58, 0.78, dirtNoise);
        diffuseColor.rgb = mix(diffuseColor.rgb, uMottleDirt, dirtMask * grassness * 0.40);

        // Steep-slope ROCK TEXTURE. Rock mask from the world normal, matching the
        // brown band in terrain.ts (CLIFF_SLOPE_BAND). On flat ground / the skirt
        // this is 0 → no cost to the look.
        vec3 wn = normalize(vTerrainWorldNormal);
        float rockMask = smoothstep(uRockBand.x, uRockBand.y, 1.0 - wn.y);
        if (rockMask > 0.001) {
          vec3 rockTexRGB = triplanarRock(vTerrainWorldPos, wn, uRockScale);
          // Normalise so the texture's mean is ~1 → a pure multiplier that keeps
          // the band's overall brightness and only adds plate relief + rocky chroma.
          vec3 rockMod = rockTexRGB / max(uRockMeanLum, 0.001);
          float rockL = dot(rockMod, vec3(0.299, 0.587, 0.114));
          rockMod = mix(rockMod, vec3(rockL), uRockDesat);   // toward greyscale relief
          vec3 modulator = mix(vec3(1.0), rockMod, uRockStrength);
          diffuseColor.rgb *= mix(vec3(1.0), modulator, rockMask);
        }`,
      );
  };
}
